// Phase 9 (subset) — Adherence Watchlist.
//
// Patients ranked by adherence/readmission risk score.  Tier filter
// (high / med / low / all) plus "Recompute all" for admins.
//
// Score is sourced from patient_risk and reflects the 3-factor subset
// shipped in Phase 9 (polypharmacy, age ≥ 75, high-risk drug class).
// Max achievable is currently 22 pts.  See docs/PHASE9_DESIGN.md.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

const TIER_BADGE = {
  high: { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca', label: 'High' },
  med:  { bg: '#fef3c7', fg: '#92400e', border: '#fde68a', label: 'Medium' },
  low:  { bg: '#d1fae5', fg: '#065f46', border: '#a7f3d0', label: 'Low' },
};

function TierBadge({ tier }) {
  const s = TIER_BADGE[tier] || TIER_BADGE.low;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border"
          style={{ background: s.bg, color: s.fg, borderColor: s.border }}>
      {s.label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function Watchlist() {
  const tHref = useTenantHref();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tier, setTier] = useState('high');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const r = await api.watchlist(tier ? { tier } : {});
      setItems(r.items || []);
    } catch (e) {
      setErr(e.message || 'failed to load watchlist');
    } finally {
      setLoading(false);
    }
  }

  async function recomputeAll() {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await api.recomputeAllRisk();
      setMsg(`Recomputed ${r.updated} of ${r.considered} patients.`);
      await load();
    } catch (e) {
      setErr(e.message || 'recompute failed');
    } finally { setBusy(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier]);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Adherence Watchlist</h1>
          <p className="text-sm text-slate-500">
            Patients ranked by adherence / readmission risk.
            <span className="ml-1 text-slate-400">3-factor subset · max 22 pts.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">Tier</label>
          <select className="input" value={tier} onChange={e => setTier(e.target.value)}>
            <option value="high">High</option>
            <option value="med">Medium</option>
            <option value="low">Low</option>
            <option value="">All tiers</option>
          </select>
          {isAdmin && (
            <button className="btn-ghost" disabled={busy} onClick={recomputeAll}>
              {busy ? 'Recomputing…' : 'Recompute all'}
            </button>
          )}
        </div>
      </div>

      {err && <div className="bg-red-50 text-red-800 border border-red-200 p-3 rounded mb-3">{err}</div>}
      {msg && <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 p-3 rounded mb-3">{msg}</div>}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            No patients in this tier yet.
            {isAdmin && <> Try <button className="underline" onClick={recomputeAll}>Recompute all</button>.</>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2">Patient</th>
                  <th className="px-3 py-2">MRN</th>
                  <th className="px-3 py-2">Room</th>
                  <th className="px-3 py-2 text-right">Score</th>
                  <th className="px-3 py-2">Tier</th>
                  <th className="px-3 py-2">Top factors</th>
                  <th className="px-3 py-2">Computed</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.patient_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link to={tHref(`/patients/${p.patient_id}`)}
                            className="font-medium hover:underline"
                            style={{ color: 'var(--brand-primary)' }}>
                        {p.first_name} {p.last_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{p.mrn}</td>
                    <td className="px-3 py-2">{p.room_number || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold">{p.score}</td>
                    <td className="px-3 py-2"><TierBadge tier={p.tier} /></td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {(p.top_factors || []).map(f => f.label).join(' · ') || '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{timeAgo(p.computed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Advisory only.  This score is a triage aid and does not constitute a clinical assessment.
      </p>
    </div>
  );
}
